import React from 'react';
import { usersApi, type UserOrderRow, type UserPaymentsInfo } from '../../../api/users';
import {
  EmptyState,
  Muted,
  RowCard,
  SectionTitle,
  StatCard,
  StatusPill,
  TabLoader,
  formatDateTime,
  formatMoney,
  useUserTabData,
  type Tone,
} from './userTabShared';

interface Props {
  userId: string;
  isDark: boolean;
}

const ORDER_STATUS: Record<UserOrderRow['status'], { label: string; tone: Tone }> = {
  pending: { label: 'Ожидает', tone: 'amber' },
  paid: { label: 'Оплачен', tone: 'green' },
  cancelled: { label: 'Отменён', tone: 'red' },
  refunded: { label: 'Возврат', tone: 'purple' },
};

const PAYMENT_STATUS: Record<string, { label: string; tone: Tone }> = {
  initiated: { label: 'Начат', tone: 'amber' },
  success: { label: 'Успешно', tone: 'green' },
  failed: { label: 'Ошибка', tone: 'red' },
  expired: { label: 'Истёк', tone: 'slate' },
};

const SUBSCRIPTION_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: 'Активна', tone: 'green' },
  expired: { label: 'Истекла', tone: 'slate' },
  cancelled: { label: 'Отменена', tone: 'red' },
};

export function UserPaymentsTab({ userId, isDark }: Props) {
  const { data, loading } = useUserTabData<UserPaymentsInfo>(
    userId,
    usersApi.getUserPayments,
    'Не удалось загрузить оплаты',
  );

  if (loading) return <TabLoader />;
  if (!data) return <EmptyState isDark={isDark}>Нет данных.</EmptyState>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Заказы" value={data.totals.ordersCount} isDark={isDark} />
        <StatCard
          label="Оплачено"
          value={formatMoney(data.totals.paidSum)}
          sublabel={`${data.totals.paidCount} успешных`}
          isDark={isDark}
        />
        <StatCard
          label="Вариант цен"
          value={data.pricingVariant?.name ?? '—'}
          sublabel={data.pricingVariant ? `с ${formatDateTime(data.pricingVariant.assignedAt)}` : undefined}
          isDark={isDark}
        />
      </div>

      <div>
        <SectionTitle isDark={isDark}>Подписки</SectionTitle>
        {data.subscriptions.length === 0 ? (
          <EmptyState isDark={isDark}>Подписок не было.</EmptyState>
        ) : (
          <div className="space-y-2">
            {data.subscriptions.map((s) => {
              const st = SUBSCRIPTION_STATUS[s.status] ?? { label: s.status, tone: 'slate' as Tone };
              return (
                <RowCard key={s.id} isDark={isDark} className="flex items-center justify-between gap-4">
                  <span className="text-sm">
                    {formatDateTime(s.startsAt)} → {formatDateTime(s.endsAt)}
                  </span>
                  <StatusPill tone={st.tone}>{st.label}</StatusPill>
                </RowCard>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <SectionTitle isDark={isDark}>Заказы и попытки оплаты</SectionTitle>
        {data.orders.length === 0 ? (
          <EmptyState isDark={isDark}>Заказов не было.</EmptyState>
        ) : (
          <div className="space-y-2">
            {data.orders.map((o) => {
              const st = ORDER_STATUS[o.status] ?? { label: o.status, tone: 'slate' as Tone };
              return (
                <RowCard key={o.id} isDark={isDark} className="space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{o.planTitle ?? o.planCode ?? 'Тариф'}</div>
                      <Muted isDark={isDark}>
                        {formatDateTime(o.createdAt)}
                        {o.promoCode && ` · промокод ${o.promoCode}`}
                        {o.discountAmount > 0 && ` · скидка ${formatMoney(o.discountAmount, o.currency)}`}
                      </Muted>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold">{formatMoney(o.finalAmount, o.currency)}</span>
                      <StatusPill tone={st.tone}>{st.label}</StatusPill>
                    </div>
                  </div>
                  {o.payments.length > 0 && (
                    <div className="space-y-1 pl-3 border-l border-slate-500/30">
                      {o.payments.map((p) => {
                        const ps = PAYMENT_STATUS[p.status] ?? { label: p.status, tone: 'slate' as Tone };
                        return (
                          <div key={p.id} className="flex items-center justify-between gap-4">
                            <Muted isDark={isDark}>
                              <span className="uppercase font-medium">{p.provider}</span> · {formatDateTime(p.createdAt)}
                              {p.otpSentPhone && ` · SMS на ${p.otpSentPhone}`}
                            </Muted>
                            <StatusPill tone={ps.tone}>{ps.label}</StatusPill>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </RowCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
